import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import { getOutlets, getLADOutlets, getLADStats } from './data/sheets';
import MapView from './MapView';
import Navbar from './Navbar';
import { calculateZoom } from './utils/mapUtils';

const DashboardView = React.lazy(() => import('./DashboardView'));
const MethodologyView = React.lazy(() => import('./MethodologyView'));

const App = () => {
  const [constituencyData, setConstituencyData] = useState(null);
  const [ladData, setLadData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [hexLayoutData, setHexLayoutData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [outlets, setOutlets] = useState([]);
  const [ladOutlets, setLadOutlets] = useState([]);
  const [ladStats, setLadStats] = useState([]);
  const [filteredOutlets, setFilteredOutlets] = useState([]);
  const [activeConstituency, setActiveConstituency] = useState(null);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [boundaryType, setBoundaryType] = useState('lad'); // 'constituency' or 'lad'

  useEffect(() => {
    // Fetch all data once on mount
    const loadData = async () => {
      try {
        const [
          outletsResp,
          ladOutletsResp,
          ladStatsResp,
          constDataResp,
          ladDataResp,
          statsDataResp,
          hexDataResp
        ] = await Promise.all([
          getOutlets(),
          getLADOutlets(),
          getLADStats(),
          fetch(`${process.env.PUBLIC_URL}/data/constituency_boundaries.json`).then(r => r.json()),
          fetch(`${process.env.PUBLIC_URL}/data/lad_boundaries.json`).then(r => r.json()),
          fetch(`${process.env.PUBLIC_URL}/data/constituency_stats.json`).then(r => r.json()),
          fetch(`${process.env.PUBLIC_URL}/data/hex_layout_2024.json`).then(r => r.json())
        ]);

        setOutlets(outletsResp);
        setLadOutlets(ladOutletsResp);
        setLadStats(ladStatsResp);
        setConstituencyData(calculateZoom(constDataResp));
        setLadData(calculateZoom(ladDataResp));
        setStatsData(statsDataResp);
        setHexLayoutData(hexDataResp);

        setFilteredOutlets(outletsResp);

        setLoading(false);
      } catch (error) {
        console.error("Error loading data:", error);
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const data = boundaryType === 'constituency' ? outlets : ladOutlets;
    setFilteredOutlets(data);
  }, [boundaryType, outlets, ladOutlets]);

  const currentOutlets = boundaryType === 'constituency' ? outlets : ladOutlets;

  return (
    <Router basename={process.env.PUBLIC_URL}>
      <div className="app-root">
        <Navbar />
        <main className="app-content">
          {loading ? (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
              <p>Loading map data...</p>
            </div>
          ) : (
            <React.Suspense fallback={
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <p>Loading view...</p>
              </div>
            }>
              <Routes>
                <Route
                  path="/"
                  element={
                    <MapView
                      outlets={currentOutlets}
                      filteredOutlets={filteredOutlets}
                      setFilteredOutlets={setFilteredOutlets}
                      activeConstituency={activeConstituency}
                      setActiveConstituency={setActiveConstituency}
                      selectedOutlet={selectedOutlet}
                      setSelectedOutlet={setSelectedOutlet}
                      boundaryType={boundaryType}
                      setBoundaryType={setBoundaryType}
                      ladStats={ladStats}
                      constituencyData={constituencyData}
                      ladData={ladData}
                      statsData={statsData}
                      hexLayoutData={hexLayoutData}
                    />
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <DashboardView
                      outlets={currentOutlets}
                      boundaryType={boundaryType}
                      ladStats={ladStats}
                      constituencyData={constituencyData}
                      ladData={ladData}
                      statsData={statsData}
                    />
                  }
                />
                <Route
                  path="/methodology"
                  element={<MethodologyView />}
                />
              </Routes>
            </React.Suspense>
          )}
        </main>
      </div>
    </Router>
  );
};

export default App;
